

import React, { useState, useEffect, useCallback } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Pagination,
  Card,
  TextField,
  CircularProgress,
  Box,
  Stack,
  Tooltip,
  Typography,
  Paper,
  Container,
  InputAdornment,
  IconButton,
  Avatar,
  alpha,
  useTheme,
  Skeleton,
  Chip,
  useMediaQuery,
} from "@mui/material";
import {
  MdSearch,
  MdClear,
  MdCategory,
  MdInbox,
  MdFilterList,
  MdViewList,
  MdArrowForward,
} from "react-icons/md";
import axios from "axios";
import debounce from "lodash.debounce";
import { useNavigate } from "react-router-dom";

const CategoryList = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchLoading, setSearchLoading] = useState(false);

  // Medstown brand colors
  const medstownColors = {
    primary: "#014D4D",
    secondary: "#02A0A0",
    accent: "#FFB800",
    success: "#4caf50",
    error: "#f44336",
    warning: "#ff9800",
  };

  // Debounced fetchData
  const fetchData = useCallback(
    debounce(async (page, query) => {
      setLoading(true);
      try {
        const { data } = await axios.get(
          `https://api.medstown.com/api/medshubs/upcategorise?page=${page}&limit=10${
            query ? `&search=${query}` : ""
          }`
        );
        setData(data.data.list || []);
        setTotalPages(data.data.totalPages || 1);
      } catch (error) {
        console.error("Error fetching data:", error);
        setData([]);
      } finally {
        setLoading(false);
      }
    }, 300),
    []
  );

  useEffect(() => {
    fetchData(currentPage, searchQuery);
  }, [currentPage, searchQuery, fetchData]);

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
    setSearchLoading(true);
    setTimeout(() => setSearchLoading(false), 300);
  };

  const clearSearch = () => {
    setSearchQuery("");
    setCurrentPage(1);
  };

  if (loading && data.length === 0) {
    return (
      <Container maxWidth="xl" sx={{ py: 1.5 }}>
        <Card sx={{ borderRadius: 2, overflow: "hidden" }}>
          <Box sx={{ p: 1.5 }}>
            <Skeleton
              variant="rectangular"
              height={40}
              sx={{ borderRadius: 1.5, mb: 1 }}
            />
          </Box>
          <Stack spacing={1} sx={{ p: 1.5 }}>
            {Array.from(new Array(6)).map((_, index) => (
              <Skeleton
                key={index}
                variant="rectangular"
                height={48}
                sx={{ borderRadius: 1 }}
              />
            ))}
          </Stack>
        </Card>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 1.5 }}>
      {/* Enhanced Header with Advanced Design */}
      {!isMobile ? (
        <Paper
          elevation={0}
          sx={{
            p: 2, // Reduced padding
            mb: 1.5, // Reduced margin
            borderRadius: 2, // Smaller border radius
            background: `linear-gradient(135deg, ${medstownColors.primary}, ${medstownColors.secondary})`,
            color: "white",
            position: "relative",
            overflow: "hidden",
          }}
        >
          {/* Background Pattern */}
          <Box
            sx={{
              position: "absolute",
              top: 0,
              right: 0,
              width: "100%",
              height: "100%",
              opacity: 0.1,
              background: `
              radial-gradient(circle at 20% 80%, rgba(255, 255, 255, 0.1) 0%, transparent 50%),
              radial-gradient(circle at 80% 20%, rgba(255, 255, 255, 0.1) 0%, transparent 50%)
            `,
            }}
          />

          <Stack
            direction="row"
            alignItems="center"
            spacing={2}
            sx={{ position: "relative", zIndex: 1 }}
          >
            {/* Enhanced Logo/Icon */}
            <Box sx={{ position: "relative" }}>
              <Avatar
                sx={{
                  backgroundColor: alpha("#ffffff", 0.2),
                  color: "white",
                  width: 40, // Smaller avatar
                  height: 40,
                  boxShadow: `0 4px 12px ${alpha("#000000", 0.15)}`,
                }}
              >
                <MdCategory size={20} /> {/* Smaller icon */}
              </Avatar>
              {/* Status indicator */}
              <Box
                sx={{
                  position: "absolute",
                  bottom: -1,
                  right: -1,
                  width: 14, // Smaller indicator
                  height: 14,
                  backgroundColor: medstownColors.success,
                  borderRadius: "50%",
                  border: "2px solid white",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Typography
                  variant="caption"
                  sx={{ fontSize: "0.5rem", fontWeight: 700 }}
                >
                  {data.length > 99 ? "99+" : data.length}
                </Typography>
              </Box>
            </Box>

            {/* Enhanced Title Section */}
            <Box sx={{ flexGrow: 1 }}>
              <Typography
                variant="h5"
                fontWeight={800}
                sx={{
                  mb: 0.3, // Reduced margin
                  textShadow: "0 1px 2px rgba(0,0,0,0.1)",
                  letterSpacing: "-0.3px",
                  fontSize: "1.3rem", // Smaller font
                }}
              >
                Category Management
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  opacity: 0.9,
                  fontSize: "0.85rem", // Smaller font
                  fontWeight: 400,
                }}
              >
                Product category hierarchy and organization
              </Typography>
            </Box>

            {/* Enhanced Stats Section */}
            <Box textAlign="right">
              <Stack spacing={0.5} alignItems="flex-end">
                <Box>
                  <Typography
                    variant="caption"
                    sx={{ opacity: 0.8, fontSize: "0.7rem" }}
                  >
                    Total Categories
                  </Typography>
                  <Typography
                    variant="h4"
                    fontWeight={800}
                    sx={{
                      lineHeight: 1,
                      textShadow: "0 1px 2px rgba(0,0,0,0.1)",
                      fontSize: "1.8rem", // Smaller font
                    }}
                  >
                    {data.length}
                  </Typography>
                </Box>
              </Stack>
            </Box>
          </Stack>
        </Paper>
      ) : (
        <Paper
          elevation={0}
          sx={{
            p: 2, // Reduced padding
            mb: 1.5, // Reduced margin
            borderRadius: 2, // Smaller border radius
            background: `linear-gradient(135deg, ${medstownColors.primary}, ${medstownColors.secondary})`,
            color: "white",
            position: "relative",
            overflow: "hidden",
          }}
        >
          <Box sx={{display:'flex',gap:2}}>
             <Avatar
                sx={{
                  backgroundColor: alpha("#ffffff", 0.2),
                  color: "white",
                  width: 40, // Smaller avatar
                  height: 40,
                  boxShadow: `0 4px 12px ${alpha("#000000", 0.15)}`,
                }}
              >
                <MdCategory size={20} /> {/* Smaller icon */}
              </Avatar>
               <Typography
                variant="h5"
                fontWeight={500}
                sx={{
                  // fontSize:"14px",
                  mt:1,
                  mb: 0.3, // Reduced margin
                  textShadow: "0 1px 2px rgba(0,0,0,0.1)",
                  letterSpacing: "-0.3px",
                  fontSize: "1rem", // Smaller font
                }}
              >
                Category Management
              </Typography>
          </Box>
        </Paper>
      )}

      {/* Enhanced Card with Compact Design */}
      <Card
        elevation={0}
        sx={{
          borderRadius: 2,
          border: `1px solid ${alpha(medstownColors.primary, 0.08)}`,
          overflow: "hidden",
        }}
      >
        {/* Compact Header Section */}
        <Box
          sx={{
            p: 1.5, // Reduced padding
            backgroundColor: alpha(medstownColors.primary, 0.06),
            borderBottom: `1px solid ${alpha(medstownColors.primary, 0.1)}`,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Avatar
              sx={{
                backgroundColor: alpha(medstownColors.primary, 0.1),
                color: medstownColors.primary,
                width: 24, // Smaller avatar
                height: 24,
              }}
            >
              <MdFilterList size={14} /> {/* Smaller icon */}
            </Avatar>
            <Typography
              variant="body1"
              fontWeight={700}
              color="primary.main"
              sx={{ fontSize: "0.85rem" }}
            >
              Category Database
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontSize: "0.65rem" }}
            >
              Browse and manage product categories
            </Typography>
          </Stack>
        </Box>

        {/* Compact Search Section */}
        <Box sx={{ p: 1.5 }}>
          {" "}
          {/* Reduced padding */}
          <TextField
            variant="outlined"
            size="small"
            fullWidth
            placeholder="Search by Category ID and Name..."
            value={searchQuery}
            onChange={handleSearch}
            sx={{
              "& .MuiOutlinedInput-root": {
                backgroundColor: alpha(theme.palette.background.paper, 0.8),
                borderRadius: 1.5, // Smaller border radius
                fontSize: "0.8rem", // Smaller font
                "& fieldset": {
                  borderColor: alpha(medstownColors.primary, 0.2),
                },
                "&:hover fieldset": {
                  borderColor: alpha(medstownColors.primary, 0.4),
                },
                "&.Mui-focused fieldset": {
                  borderColor: medstownColors.primary,
                },
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  {searchLoading ? (
                    <CircularProgress
                      size={16}
                      sx={{ color: medstownColors.primary }}
                    />
                  ) : (
                    <MdSearch size={16} color={medstownColors.primary} />
                  )}
                </InputAdornment>
              ),
              endAdornment: searchQuery && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={clearSearch}
                    sx={{ width: 20, height: 20 }}
                  >
                    <MdClear size={14} />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {/* Enhanced Table Container */}
        <TableContainer>
          <Table size="small">
            {" "}
            {/* Compact table */}
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    fontWeight: 800,
                    color: medstownColors.primary,
                    fontSize: "0.7rem", // Smaller font
                    textTransform: "uppercase",
                    letterSpacing: "0.3px",
                    py: 1, // Reduced padding
                    px: 1.5,
                    backgroundColor: alpha(medstownColors.primary, 0.04),
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <MdViewList size={12} />
                    <span>Category ID</span>
                  </Stack>
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 800,
                    color: medstownColors.primary,
                    fontSize: "0.7rem", // Smaller font
                    textTransform: "uppercase",
                    letterSpacing: "0.3px",
                    py: 1, // Reduced padding
                    px: 1.5,
                    backgroundColor: alpha(medstownColors.primary, 0.04),
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <MdCategory size={12} />
                    <span>Category Name</span>
                  </Stack>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={2} align="center" sx={{ py: 2 }}>
                    <Stack alignItems="center" spacing={1}>
                      <CircularProgress
                        size={24}
                        sx={{ color: medstownColors.primary }}
                      />
                      <Typography variant="caption" sx={{ fontSize: "0.7rem" }}>
                        Loading categories...
                      </Typography>
                    </Stack>
                  </TableCell>
                </TableRow>
              ) : data.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={2} align="center" sx={{ py: 3 }}>
                    <Stack direction="column" alignItems="center" spacing={1}>
                      <Avatar
                        sx={{
                          backgroundColor: alpha(medstownColors.primary, 0.1),
                          color: medstownColors.primary,
                          width: 40,
                          height: 40,
                        }}
                      >
                        <MdInbox size={20} />
                      </Avatar>
                      <Typography
                        variant="body2"
                        color="text.primary"
                        fontWeight={600}
                        sx={{ fontSize: "0.8rem" }}
                      >
                        No categories found
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontSize: "0.65rem" }}
                      >
                        {searchQuery
                          ? "Try adjusting your search terms"
                          : "Categories will appear here when available"}
                      </Typography>
                    </Stack>
                  </TableCell>
                </TableRow>
              ) : (
                data.map((item) => (
                  <TableRow
                    key={item.upcat}
                    hover
                    sx={{
                      "&:hover": {
                        backgroundColor: alpha(medstownColors.primary, 0.02),
                        transform: "translateY(-1px)",
                        boxShadow: `0 2px 8px ${alpha(
                          medstownColors.primary,
                          0.08
                        )}`,
                      },
                      transition: "all 0.2s ease",
                      cursor: "pointer",
                    }}
                    onClick={() =>
                      navigate(`/admin/sub-categories/${item.upcat}`)
                    }
                  >
                    <TableCell sx={{ py: 1, px: 1.5 }}>
                      {" "}
                      {/* Reduced padding */}
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Avatar
                          sx={{
                            backgroundColor: alpha(
                              medstownColors.secondary,
                              0.1
                            ),
                            color: medstownColors.secondary,
                            width: 24, // Smaller avatar
                            height: 24,
                          }}
                        >
                          <MdViewList size={12} />
                        </Avatar>
                        <Box>
                          <Typography
                            variant="body2"
                            fontWeight={600}
                            sx={{ fontSize: "0.75rem" }}
                          >
                            {item.upcat}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontSize: "0.65rem" }}
                          >
                            Click to view subcategories
                          </Typography>
                        </Box>
                      </Stack>
                    </TableCell>
                    <TableCell
                      sx={{
                        maxWidth: 200,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        py: 1, // Reduced padding
                        px: 1.5,
                      }}
                    >
                      <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                      >
                        <Typography
                          variant="body2"
                          sx={{ fontSize: "0.75rem" }}
                        >
                          {item.upcat_name}
                        </Typography>
                        <Tooltip title="View subcategories">
                          <IconButton
                            size="small"
                            sx={{
                              color: medstownColors.primary,
                              backgroundColor: alpha(
                                medstownColors.primary,
                                0.1
                              ),
                              width: 20, // Smaller button
                              height: 20,
                              "&:hover": {
                                backgroundColor: alpha(
                                  medstownColors.primary,
                                  0.2
                                ),
                                transform: "scale(1.1)",
                              },
                            }}
                          >
                            <MdArrowForward size={10} />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Compact Pagination */}
        {totalPages > 1 && (
          <Box
            sx={{
              p: 1.5,
              backgroundColor: alpha(medstownColors.primary, 0.02),
            }}
          >
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontSize: "0.65rem" }}
              >
                Page {currentPage} of {totalPages} • {data.length} categories
              </Typography>
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={handlePageChange}
                size="small"
                sx={{
                  "& .MuiPaginationItem-root": {
                    fontSize: "0.7rem",
                    minWidth: "24px",
                    height: "24px",
                    margin: "0 1px",
                  },
                  "& .Mui-selected": {
                    backgroundColor: `${medstownColors.primary} !important`,
                    color: "white",
                  },
                }}
              />
            </Stack>
          </Box>
        )}
      </Card>
    </Container>
  );
};

export default CategoryList;
